import {
  AggressorFlagEnum,
  SideEnum,
  type OrderIntent,
  type SecurityMarketDataCellPriceActions,
  type SecurityMarketDataGetPriceActionsContext,
} from '@talos/kyoko';
import type { MouseEvent } from 'react';
import { getLegIndexFromPriceGridMarketDataColumnField } from '../../../columns/priceGridMarketDataColumnUtils';
import type { PriceGridSecurityMarketDataColumnParams } from '../../../columns/types';
import type { PriceGridEntity } from '../../../PriceGridEntity';
import { getPriceGridColumnMarketSelectorValue } from './getPriceGridColumnMarketSelectorValue';

export function getPriceGridMarketDataPriceActions({
  params,
  symbol,
  columnParams,
}: SecurityMarketDataGetPriceActionsContext<PriceGridEntity, PriceGridSecurityMarketDataColumnParams>):
  | SecurityMarketDataCellPriceActions
  | undefined {
  const onPrimeOrderClick = columnParams?.onPrimeOrderClick;
  const onCancelClickToTradePreview = columnParams?.onCancelClickToTradePreview;
  const row = params.value ?? params.data;
  if (!onPrimeOrderClick || !row) {
    return undefined;
  }

  const orderQty = row.bucket.size;
  const columnID = params.column?.getColId();
  const marketSelectorValue = getPriceGridColumnMarketSelectorValue(params);
  const legIndex = getLegIndexFromPriceGridMarketDataColumnField(params.colDef?.field);

  const primeFromLevel = (side: SideEnum, price: string | undefined, intent: OrderIntent = AggressorFlagEnum.Active) =>
    onPrimeOrderClick({
      data: row,
      symbol,
      columnID,
      intent,
      side,
      price,
      orderQty,
      marketSelectorValue,
      ...(legIndex == null ? {} : { legIndex }),
    });
  const cancelPreview = (side: SideEnum, event: MouseEvent<Element>) => {
    // Ignore synthetic teardown events from AG Grid row recycling, but still
    // cancel when the pointer leaves the document/window from a connected cell.
    if (event.relatedTarget == null && !event.currentTarget.isConnected) {
      return;
    }
    onCancelClickToTradePreview?.({ rowID: row.rowID, side });
  };

  return {
    onBidPriceClick: bid => primeFromLevel(SideEnum.Sell, bid.Price),
    onOfferPriceClick: offer => primeFromLevel(SideEnum.Buy, offer.Price),
    onBidPriceContextMenu: bid => primeFromLevel(SideEnum.Buy, bid.Price, AggressorFlagEnum.Passive),
    onOfferPriceContextMenu: offer => primeFromLevel(SideEnum.Sell, offer.Price, AggressorFlagEnum.Passive),
    onBidPriceMouseLeave: event => {
      cancelPreview(SideEnum.Sell, event);
      cancelPreview(SideEnum.Buy, event);
    },
    onOfferPriceMouseLeave: event => {
      cancelPreview(SideEnum.Buy, event);
      cancelPreview(SideEnum.Sell, event);
    },
  };
}
